// Frontend: TipPopup/TipPopup.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Classes from '../RecordTable.module.css';
import backend from '../../../../../../../../api';

const PERCENTS = [10, 15, 20, 25];

function toMoney(n) {
  const x = Number(n || 0);
  try {
    return x.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  } catch {
    return `$${x.toFixed(2)}`;
  }
}

/**
 * Works in TWO modes:
 * 1) Modal mode (opened from dropdown):
 *    - props.booking, props.closePopup, props.submitPopup, props.busy passed in
 * 2) Page mode (/tip/:bookingId):
 *    - No props passed. Component fetches booking by bookingId, handles submit + close itself.
 */
export default function TipPopup(props) {
  const {
    booking: bookingProp,
    closePopup: closePopupProp,
    submitPopup: submitPopupProp,
    busy: busyProp,
  } = props || {};

  const navigate = useNavigate();
  const { bookingId: routeBookingId } = useParams();
  const { token } = useSelector((s) => s?.auth || {});

  const [booking, setBooking] = useState(bookingProp || null);
  const [loading, setLoading] = useState(!bookingProp && !!routeBookingId);
  const [busy, setBusy] = useState(!!busyProp);

  // When used as a page, fetch booking details
  useEffect(() => {
    let mounted = true;
    async function fetchBooking() {
      if (!routeBookingId || bookingProp) return;
      setLoading(true);
      try {
        const { data } = await backend.get(`/api/bookings/${routeBookingId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!mounted) return;
        setBooking(data?.data || null);
      } catch (e) {
        console.error('tip fetch booking error', e);
        if (mounted) setBooking(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchBooking();
    return () => { mounted = false; };
  }, [routeBookingId, bookingProp, token]);

  // Use booking.pricing.total as base (what the customer paid)
  const baseTotal = useMemo(() => {
    const total = Number(booking?.pricing?.total || 0);
    const priorTip = Number(booking?.tip || 0);
    // choose your base preference:
    // return Math.max(0, total - priorTip);   // calc from pre-tip amount
    return total;                               // calc from full total
  }, [booking]);

  const [activePercent, setActivePercent] = useState(15);
  const [custom, setCustom] = useState('');

  const computedTip = useMemo(() => {
    if (custom && Number(custom) > 0) return Number(custom);
    return Number(((activePercent / 100) * baseTotal).toFixed(2));
  }, [activePercent, baseTotal, custom]);

  const newGrandTotal = useMemo(
    () => Number((baseTotal + computedTip).toFixed(2)),
    [baseTotal, computedTip]
  );

  const isCompleted = (booking?.status || '').toLowerCase() === 'completed';
  const alreadyTipped = Number(booking?.tip || 0) > 0;

  const canTip = isCompleted; // you can also AND !alreadyTipped if you want to block repeat tips.

  // Close behavior:
  const close = () => {
    if (closePopupProp) return closePopupProp();              // modal mode
    navigate('/appointments');                                // page mode
  };

  // Submit behavior:
  const handleSubmit = async (amount) => {
    if (submitPopupProp) return submitPopupProp(amount);      // modal mode

    // page mode: call backend and redirect to stripe
    if (!booking?._id) return;
    try {
      setBusy(true);
      const { data } = await backend.post(
        '/pay/create-tip-session',
        { bookingId: booking._id, tipAmount: amount },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        alert('Failed to create tip session.');
      }
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = () => {
    if (!computedTip || computedTip <= 0) {
      alert('Please enter a valid tip amount.');
      return;
    }
    if (!canTip) {
      alert('Tips are only available once the appointment is completed.');
      return;
    }
    handleSubmit(computedTip);
  };

  // ESC to close (works in both modes)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line

  if (loading) {
    return (
      <div className={Classes.tipPageLoading}>
        <div className={Classes.tipSpinner} />
        <h5>Loading tip details…</h5>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className={Classes.tipPageLoading}>
        <h5>Unable to load this booking.</h5>
        <button type="button" className={Classes.btnGhost} onClick={close}>Back</button>
      </div>
    );
  }

  return (
    <motion.div
      className={Classes.tipModal}            // overlay style (works as page or modal)
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className={Classes.tipCard}
        initial={{ scale: 0.96, y: 10, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.96, y: 10, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 240, damping: 24 }}
      >
        <div className={Classes.tipHeader}>
          <h4>Add a tip</h4>
          {!canTip && (
            <p className={Classes.tipNotice}>
              Tips are available only after the appointment is marked <strong>Completed</strong>.
            </p>
          )}
          {alreadyTipped && (
            <p className={Classes.tipNotice}>
              You have already tipped {toMoney(booking?.tip)}. You can still add an additional tip below.
            </p>
          )}
          {canTip && !alreadyTipped && (
            <p>Show appreciation for your driver. Choose a percentage or enter a custom amount.</p>
          )}
        </div>

        <div className={Classes.tipBaseRow}>
          <span>Order total</span>
          <strong>{toMoney(baseTotal)}</strong>
        </div>

        <div className={Classes.tipPercentRow}>
          {PERCENTS.map((p) => (
            <button
              key={p}
              className={`${Classes.tipChip} ${activePercent === p && !custom ? Classes.tipChipActive : ''}`}
              onClick={() => { setActivePercent(p); setCustom(''); }}
              disabled={busy || !canTip}
              type="button"
            >
              {p}%
              <span className={Classes.tipChipSub}>{toMoney((p / 100) * baseTotal)}</span>
            </button>
          ))}
        </div>

        <div className={Classes.tipInputRow}>
          <label htmlFor="customTip">Custom tip</label>
          <div className={Classes.tipInputWrap}>
            <span>$</span>
            <input
              id="customTip"
              type="number"
              min="0.01"
              step="0.01"
              inputMode="decimal"
              placeholder="0.00"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              disabled={busy || !canTip}
            />
            {custom && Number(custom) > 0 && (
              <button
                type="button"
                className={Classes.clearBtn}
                onClick={() => setCustom('')}
                disabled={busy}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div className={Classes.tipSummary}>
          <div><span>Tip</span><strong>{toMoney(computedTip)}</strong></div>
          <div><span>New total</span><strong>{toMoney(newGrandTotal)}</strong></div>
        </div>

        <div className={Classes.tipActions}>
          <button type="button" className={Classes.btnGhost} onClick={close} disabled={busy}>Back</button>
          <button type="button" className={Classes.btnPrimary} onClick={submit} disabled={busy || !canTip}>
            {busy ? 'Processing…' : 'Add tip & Pay'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
